import React, { useState, useRef, useEffect } from 'react';
import { MapPin, Search, Anchor, X } from 'lucide-react';

function PortSearch({ ports, value, onChange, placeholder, testId }) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Set initial query from value
  useEffect(function() {
    if (value && !query) {
      setQuery(value);
    }
  }, [value]);

  // Filter ports based on query
  const filteredPorts = query.length >= 1 
    ? ports.filter(function(port) {
        const searchStr = (port.name + ' ' + port.country + ' ' + (port.un_locode || '')).toLowerCase();
        return searchStr.includes(query.toLowerCase());
      }).slice(0, 10)  // Limit to 10 results
    : [];

  function handleInputChange(e) {
    const newQuery = e.target.value;
    setQuery(newQuery);
    setIsOpen(newQuery.length >= 1);
    setHighlightedIndex(0);
    
    // If cleared, also clear the value
    if (!newQuery) {
      onChange('');
    }
  }

  function handleSelect(port) {
    const portValue = port.name + ', ' + port.country;
    setQuery(portValue);
    onChange(portValue);
    setIsOpen(false);
  }

  function handleKeyDown(e) {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(function(prev) {
          return prev < filteredPorts.length - 1 ? prev + 1 : prev;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(function(prev) {
          return prev > 0 ? prev - 1 : 0;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredPorts[highlightedIndex]) {
          handleSelect(filteredPorts[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      default:
        break;
    }
  }

  function handleBlur() {
    // Delay closing to allow click on suggestion
    setTimeout(function() {
      setIsOpen(false);
    }, 200);
  }

  function handleClear() {
    setQuery('');
    onChange('');
    inputRef.current?.focus();
  }

  // Scroll highlighted item into view
  useEffect(function() {
    if (listRef.current && highlightedIndex >= 0) {
      const items = listRef.current.querySelectorAll('[data-port-item]');
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-[18px] w-[18px] text-gray-600 pointer-events-none" strokeWidth={1.75} />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={function() { if (query.length >= 1) setIsOpen(true); }}
          onBlur={handleBlur}
          placeholder={placeholder || "Search port by name, country, or code..."}
          className="sv-input h-11 pl-11 pr-10"
          data-testid={testId}
          autoComplete="off"
        />
        {query && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-ink"
          >
            <X className="h-[18px] w-[18px]" strokeWidth={1.75} />
          </button>
        )}
      </div>

      {/* Suggestions Dropdown */}
      {isOpen && filteredPorts.length > 0 && (
        <div 
          ref={listRef}
          className="absolute z-50 w-full mt-1 bg-white rounded shadow-overlay border border-gray-300 max-h-80 overflow-y-auto"
        >
          {filteredPorts.map(function(port, index) {
            const isHighlighted = index === highlightedIndex;
            return (
              <div
                key={port.id || port.un_locode || index}
                data-port-item
                data-testid={(testId || 'port') + '-option-' + index}
                onClick={function() { handleSelect(port); }}
                className={"px-4 py-3 cursor-pointer transition-colors " + (
                  isHighlighted ? 'bg-blue-100' : 'hover:bg-gray-50'
                )}
              >
                <div className="flex items-center gap-3">
                  <Anchor className="h-[18px] w-[18px] text-gray-600 shrink-0" strokeWidth={1.75} />
                  <div className="flex-1">
                    <p className="text-[15px] text-ink">
                      {port.name}
                    </p>
                    <div className="flex items-center gap-2 text-[13px] text-gray-600">
                      <MapPin className="h-3 w-3" />
                      <span>{port.country}</span>
                      {port.un_locode && (
                        <>
                          <span>•</span>
                          <span className="sv-mono text-xs text-gray-700">
                            {port.un_locode}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* No results message */}
      {isOpen && query.length >= 2 && filteredPorts.length === 0 && (
        <div className="absolute z-50 w-full mt-2 bg-white rounded-sm shadow-lg border border-gray-100 p-6 text-center">
          <MapPin className="h-8 w-8 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-500">No ports found for "{query}"</p>
          <p className="text-sm text-gray-400 mt-1">Try searching by city name or country</p>
        </div>
      )}
    </div>
  );
}

export default PortSearch;
